import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/ui/PageHeader';
import Toast from '@/components/ui/Toast';
import StepUpload from './components/StepUpload';
import StepExtraction from './components/StepExtraction';
import StepReview from './components/StepReview';
import StepDiff from './components/StepDiff';
import { importsApi, type DiffPayload, type ImportVersion } from '@/lib/imports';
import { ApiError } from '@/lib/api';

/** Мастер импорта на реальных данных: загрузка → извлечение (фон, опрос) → проверка строк → дифф → публикация.
 * Текущая версия держится в ?v=<id>, чтобы страницу можно было перезагрузить и продолжить. */
export default function ImportPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const versionId = params.get('v');
  const [step, setStep] = useState(1);
  const [version, setVersion] = useState<ImportVersion | null>(null);
  const [published, setPublished] = useState<{ version: ImportVersion; diff: DiffPayload; stock: { units: number; valueAed: number } } | null>(null);
  const [toast, setToast] = useState<{ message: string; detail?: string; tone?: 'success' | 'error' } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!versionId) {
      setVersion(null);
      return;
    }
    try {
      const v = await importsApi.get(versionId);
      setVersion(v);
      setLoadError(null);
      if (v.status === 'Published') {
        setPublished({ version: v, diff: v.changes ?? { rows: [], counts: { new: 0, removed: 0, price: 0, stock: 0, priceCuts: 0, priceRises: 0 }, items: v.itemsCount ?? 0 }, stock: { units: 0, valueAed: 0 } });
      } else if (!v.finishedAt) setStep(2);
      else if (step < 2) setStep(3);
    } catch (e) {
      setLoadError((e as ApiError).code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectVersion = (v: ImportVersion, nextStep: number) => {
    setVersion(v);
    setPublished(null);
    setStep(nextStep);
    const q = new URLSearchParams(params);
    q.set('v', v.id);
    setParams(q, { replace: true });
  };

  const reset = () => {
    setPublished(null);
    setVersion(null);
    setStep(1);
    const q = new URLSearchParams(params);
    q.delete('v');
    setParams(q, { replace: true });
  };

  const steps = [
    { id: 1, label: t('imp.step1'), hint: t('imp.step1Hint') },
    { id: 2, label: t('imp.step2'), hint: version?.filePages ? t('impX.pagesHint', { count: version.filePages }) : t('imp.step2Hint') },
    { id: 3, label: t('imp.step3'), hint: version?.itemsCount != null ? t('impX.rowsHint', { count: version.itemsCount }) : t('imp.step3Hint') },
    { id: 4, label: t('imp.step4'), hint: t('imp.step4Hint') },
  ];

  return (
    <div>
      <PageHeader
        eyebrow={t('imp.eyebrow')}
        title={t('imp.title')}
        description={t('imp.description')}
        actions={
          <>
            <button
              type="button"
              onClick={() => {
                reset();
                window.setTimeout(() => document.getElementById('history')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
              }}
              className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-[12px] font-medium text-foreground-700 transition-colors hover:bg-background-100"
            >
              <i className="ri-history-line text-[14px]" />
              {t('imp.history')}
            </button>
            <button
              type="button"
              onClick={reset}
              className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-add-line text-[14px]" />
              {t('imp.newImport')}
            </button>
          </>
        }
      />

      <div className="mb-5 overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="flex flex-col divide-y divide-background-200 sm:flex-row sm:divide-x sm:divide-y-0">
          {steps.map((s) => {
            const isDone = !!published || step > s.id;
            const isActive = !published && step === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  if (published) return;
                  if (s.id <= step && (s.id === 1 || version)) setStep(s.id);
                }}
                className={`flex flex-1 items-center gap-2.5 px-4 py-3 text-left transition-colors ${
                  isActive ? 'bg-primary-50/60' : 'hover:bg-background-100/60'
                } ${!published && s.id <= step ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10.5px] font-semibold ${
                    isDone
                      ? 'bg-primary-500 text-background-50'
                      : isActive
                        ? 'border border-primary-400 bg-background-50 text-primary-700'
                        : 'border border-background-300 bg-background-50 text-foreground-400'
                  }`}
                >
                  {isDone ? <i className="ri-check-line text-[12px]" /> : s.id}
                </span>
                <div className="min-w-0">
                  <p className={`text-[12px] font-medium ${isActive || isDone ? 'text-foreground-950' : 'text-foreground-500'}`}>{s.label}</p>
                  <p className="truncate text-[10.5px] text-foreground-400">{s.hint}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {loadError && (
        <p className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[11.5px] text-rose-700">
          {t('ui.error')}: {loadError}{' '}
          <button type="button" onClick={() => void load()} className="cursor-pointer font-semibold underline">
            {t('ui.retry')}
          </button>
        </p>
      )}

      {published ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-8 text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-700">
            <i className="ri-checkbox-circle-line text-2xl" />
          </span>
          <h2 className="mt-3 text-lg font-semibold text-foreground-950">{t('imp.doneTitle', { version: published.version.version })}</h2>
          <p className="mx-auto mt-1 max-w-lg text-[12.5px] leading-5 text-foreground-500">
            {t('impX.doneBody', {
              count: published.diff.items,
              partner: published.version.partnerName ?? '',
              added: published.diff.counts.new,
              removed: published.diff.counts.removed,
              changed: published.diff.counts.price + published.diff.counts.stock,
            })}
          </p>
          <div className="mx-auto mt-4 grid max-w-lg grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              [t('imp.statItemsLive'), String(published.diff.items)],
              [t('imp.statPriceCuts'), String(published.diff.counts.priceCuts)],
              [t('imp.statPriceRises'), String(published.diff.counts.priceRises)],
              [t('impX.statStockUnits'), String(published.stock.units)],
            ].map(([label, value]) => (
              <div key={label} className="rounded-md border border-background-200 bg-background-100/60 px-2 py-2">
                <p className="text-[10px] uppercase tracking-[0.08em] text-foreground-400">{label}</p>
                <p className="tnum mt-0.5 text-[13px] font-semibold text-foreground-900">{value}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            <Link to="/catalog" className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3.5 text-[12px] font-semibold text-background-50 hover:bg-primary-600">
              <i className="ri-grid-line text-[13px]" /> {t('imp.openCatalog')}
            </Link>
            <Link to={`/partners/${published.version.partnerId}`} className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3.5 text-[12px] font-medium text-foreground-700 hover:bg-background-100">
              <i className="ri-store-2-line text-[13px]" /> {t('imp.viewPartner')}
            </Link>
            <button type="button" onClick={reset} className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3.5 text-[12px] font-medium text-foreground-700 hover:bg-background-100">
              <i className="ri-upload-cloud-2-line text-[13px]" /> {t('imp.importAnother')}
            </button>
          </div>
        </div>
      ) : (
        <>
          {step === 1 && <StepUpload onStarted={(v) => selectVersion(v, 2)} onOpenExisting={(v) => selectVersion(v, v.finishedAt ? 3 : 2)} />}
          {step === 2 && version && <StepExtraction version={version} onVersion={setVersion} onNext={() => setStep(3)} onBack={() => setStep(1)} />}
          {step === 3 && version && <StepReview version={version} onVersion={setVersion} onNext={() => setStep(4)} onBack={() => setStep(2)} />}
          {step === 4 && version && (
            <StepDiff
              version={version}
              onVersion={setVersion}
              onBack={() => setStep(3)}
              onPublished={(res) => {
                setPublished({ version: res, diff: res.diff, stock: res.stock });
                setToast({ message: t('imp.toastTitle', { version: res.version, count: res.diff.items }), detail: t('imp.toastDetail') });
              }}
              onError={(code) => setToast({ message: t('ui.error'), detail: t(`impX.publishError.${code.split(':')[0]}`, { defaultValue: code }), tone: 'error' })}
            />
          )}
        </>
      )}

      {toast && <Toast message={toast.message} detail={toast.detail} tone={toast.tone} onClose={() => setToast(null)} />}
    </div>
  );
}
